import React from 'react';
import {
    Box,
    Button,
    Grid,
    Typography,
    IconButton,
    Chip,
    Avatar,
    Dialog,
    DialogContent,
    Slide,
    Card,
    CardContent,
    Stack,
    CircularProgress,
    Fade,
    Backdrop,
    Badge,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Security, AdminPanelSettings, ManageAccounts, CheckCircle, Circle } from '@mui/icons-material';
import ProgressOverlay from '../../../../components/ProgressOverlay';
import FintechLoader from '../../../../components/FintechLoader';
import WaveAnimation from '../../../../components/WaveAnimation';

interface RoleManagementModalProps {
    open: boolean;
    onClose: () => void;
    selectedUser: any;
    rolesData: any;
    selectedRole: string;
    setSelectedRole: (role: string) => void;
    userCurrentRoles: string[];
    roleLoading: boolean;
    roleSuccess: boolean;
    currentRoleLoading: boolean;
    onRoleAssign: () => void;
    extractRoleName: (fullRoleName: string) => string;
}

const RoleManagementModal: React.FC<RoleManagementModalProps> = ({
    open,
    onClose,
    selectedUser,
    rolesData,
    selectedRole,
    setSelectedRole,
    userCurrentRoles,
    roleLoading,
    roleSuccess,
    currentRoleLoading,
    onRoleAssign,
    extractRoleName
}) => {
    const [pageLoaded, setPageLoaded] = React.useState(false);
    const isLoading = currentRoleLoading || roleLoading || !rolesData;

    React.useEffect(() => {
        if (!isLoading) {
            const timer = setTimeout(() => setPageLoaded(true), 200);
            return () => clearTimeout(timer);
        } else {
            setPageLoaded(false);
        }
    }, [isLoading]);

    return (
        <>
            {/* Modern Role Assignment Dialog */}
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                TransitionComponent={Slide}
                TransitionProps={{ direction: "down" } as any}
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        overflow: 'hidden',
                        background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                        height: '90vh',
                        maxHeight: '90vh',
                    }
                }}
                BackdropComponent={Backdrop}
                BackdropProps={{
                    sx: {
                        background: 'rgba(0, 0, 0, 0.4)',
                        backdropFilter: 'blur(10px)'
                    }
                }}
            >
                {(!pageLoaded || isLoading) ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
                        <FintechLoader />
                    </Box>
                ) : (
                    <Fade in={pageLoaded} timeout={600}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                            <DialogContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
                                {/* İçerik alanı - genel scroll yok, sadece rol listesi scroll olacak */}
                                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                {/* Header with Gradient */}
                                <Box sx={{
                                    background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #06b6d4 100%)',
                                    color: 'white',
                                    height: '80px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 4,
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}>
                                    <Box sx={{
                                        position: 'absolute',
                                        top: -50,
                                        right: -50,
                                        width: 200,
                                        height: 200,
                                        borderRadius: '50%',
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        backdropFilter: 'blur(10px)'
                                    }} />
                                    <Box sx={{
                                        position: 'absolute',
                                        bottom: -30,
                                        left: -30,
                                        width: 150,
                                        height: 150,
                                        borderRadius: '50%',
                                        background: 'rgba(255, 255, 255, 0.05)'
                                    }} />

                                    <Box sx={{ position: 'relative', zIndex: 2, width: '100%' }}>
                                        <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                                            <Avatar sx={{
                                                background: 'rgba(255, 255, 255, 0.2)',
                                                backdropFilter: 'blur(10px)',
                                                width: 56,
                                                height: 56,
                                                border: '2px solid rgba(255, 255, 255, 0.3)'
                                            }}>
                                                <Security fontSize="large" />
                                            </Avatar>
                                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.3rem !important', mb: 0.5 }}>
                                                    Rol Yönetimi
                                                </Typography>
                                            </Box>
                                            <IconButton
                                                onClick={onClose}
                                                sx={{
                                                    color: 'white',
                                                    background: 'rgba(255, 255, 255, 0.1)',
                                                    '&:hover': {
                                                        background: 'rgba(255, 255, 255, 0.2)',
                                                        transform: 'scale(1.1)'
                                                    },
                                                    transition: 'all 0.2s ease',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <CloseIcon />
                                            </IconButton>
                                        </Stack>
                                    </Box>
                                </Box>

                                {/* User Profile Card */}
                                <Box sx={{ p: 4, pb: 0 }}>
                                    <Card sx={{
                                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(147, 197, 253, 0.1) 100%)',
                                        border: '1px solid rgba(59, 130, 246, 0.1)',
                                        borderRadius: 3,
                                        overflow: 'hidden',
                                        position: 'relative'
                                    }}>
                                        <CardContent sx={{ p: 3 }}>
                                            <Stack direction="row" alignItems="center" spacing={3}>
                                                <Badge
                                                    overlap="circular"
                                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                    badgeContent={
                                                        <Avatar sx={{
                                                            width: 24,
                                                            height: 24,
                                                            background: '#22c55e',
                                                            border: '2px solid white'
                                                        }}>
                                                            <ManageAccounts sx={{ fontSize: 14 }} />
                                                        </Avatar>
                                                    }
                                                >
                                                    <Avatar sx={{
                                                        width: 64,
                                                        height: 64,
                                                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                                        fontSize: '24px',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        {selectedUser?.userName?.charAt(0)?.toUpperCase() || 'U'}
                                                    </Avatar>
                                                </Badge>

                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5, fontSize: '1.25rem' }}>
                                                        {selectedUser?.userName || 'Kullanıcı'}
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ color: '#64748b', mb: 1, fontSize: '1rem' }}>
                                                        {selectedUser?.email || 'email@example.com'}
                                                    </Typography>
                                                    {userCurrentRoles.length > 0 && (
                                                        <Chip
                                                            icon={<AdminPanelSettings />}
                                                            label={`Mevcut: ${extractRoleName(userCurrentRoles[0])}`}
                                                            variant="outlined"
                                                            size="small"
                                                            sx={{
                                                                borderColor: '#3b82f6',
                                                                color: '#1d4ed8',
                                                                background: 'rgba(59, 130, 246, 0.05)',
                                                                fontSize: '0.875rem',
                                                                '& .MuiChip-label': {
                                                                    fontSize: '0.875rem'
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                </Box>

                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography variant="body1" sx={{ color: '#64748b', display: 'block', fontSize: '1rem', mb: 0.75, fontWeight: 500 }}>
                                                        Durum
                                                    </Typography>
                                                    <Chip
                                                        label={selectedUser?.status ? 'Aktif' : 'Pasif'}
                                                        color={selectedUser?.status ? 'success' : 'error'}
                                                        size="medium"
                                                        variant="outlined"
                                                        sx={{
                                                            fontSize: '1rem',
                                                            fontWeight: 600,
                                                            '& .MuiChip-label': {
                                                                fontSize: '1rem',
                                                                fontWeight: 600
                                                            }
                                                        }}
                                                    />
                                                </Box>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Box>

                                {/* Role Selection Section - kalan alanı doldurur, sadece grid scroll */}
                                <Box sx={{ px: 4, pb: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                    <Stack spacing={2} sx={{ mt: 3, flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                        <Box sx={{ flexShrink: 0 }}>
                                            <Typography variant="h6" sx={{
                                                fontWeight: 600,
                                                color: '#1e293b',
                                                mb: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                fontSize: '1.25rem !important'
                                            }}>
                                                🎯 Yeni Rol Seçimi
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '1rem !important' }}>
                                                Kullanıcıya atanacak yeni rolü seçiniz
                                            </Typography>
                                        </Box>

                                        {/* Loading State for Current Role */}
                                        {currentRoleLoading ? (
                                            <Box sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                py: 6,
                                                minHeight: 200
                                            }}>
                                                <FintechLoader />
                                                <Typography variant="h6" sx={{
                                                    mt: 3,
                                                    color: '#374151',
                                                    fontWeight: 600
                                                }}>
                                                    Mevcut Yetkiler Yükleniyor
                                                </Typography>
                                                <Typography variant="body2" sx={{
                                                    color: '#64748b',
                                                    textAlign: 'center',
                                                    mt: 1,
                                                    maxWidth: 300
                                                }}>
                                                    {selectedUser?.firstName} {selectedUser?.lastName} kullanıcısının mevcut yetkileri kontrol ediliyor...
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <>
                                                {/* Rol kartları - sadece bu alan scroll */}
                                                <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                                                    <Grid container spacing={2} sx={{ padding: '10px' }}>
                                                        {rolesData?.data?.map((role: any, index: number) => (
                                                            <Grid item xs={12} sm={6} key={role.id}>
                                                                <WaveAnimation delay={index * 0.08} duration={0.6}>
                                                                    <Card
                                                                        onClick={() => setSelectedRole(role.name)}
                                                                        sx={{
                                                                            cursor: 'pointer',
                                                                            borderRadius: 3,
                                                                            border: selectedRole === role.name
                                                                                ? '2px solid #3b82f6'
                                                                                : '2px solid transparent',
                                                                            background: selectedRole === role.name
                                                                                ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(147, 197, 253, 0.1) 100%)'
                                                                                : 'white',
                                                                            boxShadow: selectedRole === role.name
                                                                                ? '0 10px 25px rgba(59, 130, 246, 0.15)'
                                                                                : '0 4px 12px rgba(0, 0, 0, 0.05)',
                                                                            transform: selectedRole === role.name ? 'translateY(-4px)' : 'translateY(0)',
                                                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                            position: 'relative',
                                                                            overflow: 'hidden',
                                                                            '&:hover': {
                                                                                transform: 'translateY(-2px)',
                                                                                boxShadow: '0 8px 25px rgba(59, 130, 246, 0.1)',
                                                                                borderColor: selectedRole === role.name ? '#3b82f6' : '#cbd5e1'
                                                                            }
                                                                        }}
                                                                    >
                                                                        {selectedRole === role.name && (
                                                                            <Box sx={{
                                                                                position: 'absolute',
                                                                                top: 0,
                                                                                left: 0,
                                                                                right: 0,
                                                                                height: 4,
                                                                                background: 'linear-gradient(90deg, #3b82f6 0%, #06b6d4 100%)'
                                                                            }} />
                                                                        )}

                                                                        <CardContent sx={{ p: 3 }}>
                                                                            <Stack direction="row" alignItems="center" spacing={2}>
                                                                                <Avatar sx={{
                                                                                    background: selectedRole === role.name
                                                                                        ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                                                                                        : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                                                                                    color: selectedRole === role.name ? 'white' : '#64748b',
                                                                                    width: 48,
                                                                                    height: 48,
                                                                                    fontSize: '18px',
                                                                                    fontWeight: 'bold'
                                                                                }}>
                                                                                    {extractRoleName(role.name).charAt(0)}
                                                                                </Avatar>

                                                                                <Box sx={{ flex: 1 }}>
                                                                                    <Typography variant="subtitle1" sx={{
                                                                                        fontWeight: 600,
                                                                                        color: selectedRole === role.name ? '#1d4ed8' : '#374151',
                                                                                        mb: 0.5,
                                                                                        fontSize: '1.125rem'
                                                                                    }}>
                                                                                        {extractRoleName(role.name)}
                                                                                    </Typography>
                                                                                    <Typography variant="body2" sx={{
                                                                                        color: '#94a3b8',
                                                                                        display: 'block',
                                                                                        fontSize: '0.75rem',
                                                                                        wordBreak: 'break-all'
                                                                                    }}>
                                                                                        ID: {role.id}
                                                                                    </Typography>
                                                                                </Box>

                                                                                {selectedRole === role.name ? (
                                                                                    <CheckCircle sx={{ color: '#3b82f6', fontSize: 28 }} />
                                                                                ) : (
                                                                                    <Circle sx={{ color: '#cbd5e1', fontSize: 28 }} />
                                                                                )}
                                                                            </Stack>
                                                                        </CardContent>
                                                                    </Card>
                                                                </WaveAnimation>
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </Box>

                                                {/* Seçilen rol özeti - sade bilgi şeridi */}
                                            </>
                                        )}
                                    </Stack>
                                  </Box>

                            </Box>
                                {/* Action Buttons - sabit alt alan, her zaman görünür */}
                                <Box sx={{
                                    borderTop: '1px solid #e2e8f0',
                                    p: 3,
                                    background: '#f8fafc',
                                    flexShrink: 0
                                }}>
                                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                                        <Button
                                            variant="outlined"
                                            onClick={onClose}
                                            sx={{
                                                borderColor: '#cbd5e1',
                                                color: '#64748b',
                                                fontSize: '0.9375rem',
                                                fontWeight: 600,
                                                '&:hover': {
                                                    borderColor: '#94a3b8',
                                                    background: '#f1f5f9'
                                                }
                                            }}
                                        >
                                            Vazgeç
                                        </Button>
                                        <Button
                                            variant="contained"
                                            onClick={onRoleAssign}
                                            disabled={!selectedRole || roleLoading || roleSuccess || currentRoleLoading}
                                            startIcon={roleLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Security />}
                                            sx={{
                                                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                                fontSize: '0.9375rem',
                                                fontWeight: 600,
                                                '&:hover': {
                                                    background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)'
                                                },
                                                '&:disabled': {
                                                    background: '#e2e8f0',
                                                    color: '#9ca3af'
                                                },
                                                minWidth: 140,
                                                '& .MuiButton-startIcon': {
                                                    marginRight: roleLoading ? 1 : 0.5
                                                }
                                            }}
                                        >
                                            {currentRoleLoading ? 'Yükleniyor...' : roleLoading ? 'Atanıyor...' : 'Rolü Ata'}
                                        </Button>
                                    </Stack>
                                </Box>
                            </DialogContent>
                        </Box>
                    </Fade>
                )}

                {/* Role Assignment Progress Overlay */}
                <ProgressOverlay
                    isLoading={roleLoading}
                    isSuccess={roleSuccess}
                    isVisible={roleLoading || roleSuccess}
                    loadingTitle="Rol Güncelleniyor"
                    successTitle="Rol Başarıyla Atandı!"
                    loadingMessage={`${selectedUser?.firstName || ''} ${selectedUser?.lastName || ''} için ${selectedRole ? extractRoleName(selectedRole) : ''} rolü atanıyor...`}
                    successMessage={`${extractRoleName(selectedRole || '')} rolü ${selectedUser?.firstName || ''} ${selectedUser?.lastName || ''} için başarıyla atandı.`}
                    icon={<Security sx={{ fontSize: 18, color: 'white' }} />}
                />
            </Dialog>
        </>
    );
};

export default RoleManagementModal;
